import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ApiError,
  completeTask,
  createUploadTarget,
  fetchTask,
  putToUploadUrl,
} from '../api/client';
import type { UploadPurpose } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { useGeolocation } from '../geo/useGeolocation';
import { byteLength, sha256Hex } from '../lib/checksum';
import { formatAccuracy } from '../lib/format';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import {
  advance,
  canComplete,
  completionBlockers,
  isUploaded,
  markFailed,
  newSlot,
  retrySlot,
  uploadedPhotoIds,
  uploadedSignatureId,
  validateBeforeUpload,
  withProgress,
  type UploadSlot,
} from '../lib/proof-upload';
import { ErrorCard, LoadingState } from '../states';
import { SignaturePad } from './SignaturePad';

/**
 * Proof of Delivery — bukti + penyelesaian tugas.
 *
 * Aturan yang menentukan seluruh halaman: **tombol Selesaikan tetap terkunci selama masih ada
 * berkas yang belum benar-benar terunggah.** Server menegakkannya lewat `TASK-006`; di sini ia
 * dicegah lebih dulu supaya tidak ada penyelesaian sebagian yang diam-diam — 2 dari 3 foto
 * berhasil, jaringan putus, dan tugas terlanjur ditandai selesai.
 *
 * Alur per berkas tidak dipendekkan: hitung checksum → minta target ke server → `PUT` ke
 * `uploadUrl` yang server berikan. Checksum dihitung atas byte yang SAMA dengan yang dikirim,
 * karena server mencocokkannya sebelum menandai berkas aktif.
 */
export function ProofPage(): ReactNode {
  const session = useSession();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const geo = useGeolocation();
  const keys = useMemo(() => createIdempotencyKeyHolder(), []);

  const [slots, setSlots] = useState<readonly UploadSlot[]>([]);
  const [recipientName, setRecipientName] = useState('');
  const [recipientRelation, setRecipientRelation] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  // Blob disimpan di ref, bukan state: ia tidak memengaruhi tampilan dan menyalinnya pada tiap
  // render akan menahan memori foto besar lebih lama dari perlu.
  const blobs = useRef(new Map<string, Blob>());

  const { state } = useAsync(() => fetchTask(session.auth, id), [session.auth, id]);

  const patch = (localId: string, fn: (slot: UploadSlot) => UploadSlot) =>
    setSlots((prev) => prev.map((s) => (s.localId === localId ? fn(s) : s)));

  async function upload(slot: UploadSlot, purpose: UploadPurpose): Promise<void> {
    const blob = blobs.current.get(slot.localId);
    if (!blob) {
      patch(slot.localId, (s) => markFailed(s, 'Berkas tidak ditemukan lagi. Ambil ulang.'));
      return;
    }
    try {
      patch(slot.localId, (s) => advance(s, 'hashing'));
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const checksum = await sha256Hex(bytes);

      patch(slot.localId, (s) => advance(s, 'requesting-target'));
      const target = await createUploadTarget(session.auth, {
        purpose,
        fieldTaskId: id,
        mimeType: blob.type,
        // Ukuran dan checksum diambil dari SUMBER YANG SAMA — bukan dari File.size yang bisa
        // merujuk berkas sebelum diproses.
        fileSizeBytes: byteLength(bytes),
        checksumSha256: checksum,
        originalFilename: slot.fileName,
      });

      patch(slot.localId, (s) => advance(s, 'uploading', target.fileId));
      await putToUploadUrl(target.uploadUrl, blob, (percent) =>
        patch(slot.localId, (s) => withProgress(s, percent)),
      );
      patch(slot.localId, (s) => advance(s, 'uploaded'));
    } catch (caught: unknown) {
      const message =
        caught instanceof ApiError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : 'Unggah gagal.';
      patch(slot.localId, (s) => markFailed(s, message));
    }
  }

  function addPhoto(file: File): void {
    setLocalError(null);
    const photos = slots.filter((s) => s.kind === 'photo').length;
    const problem = validateBeforeUpload(
      { mimeType: file.type, sizeBytes: file.size },
      'photo',
      photos,
    );
    if (problem) {
      setLocalError(problem);
      return;
    }
    const localId = `p-${Date.now()}-${photos}`;
    blobs.current.set(localId, file);
    const slot = newSlot({
      localId,
      kind: 'photo',
      fileName: file.name || 'foto.jpg',
      sizeBytes: file.size,
      mimeType: file.type,
    });
    setSlots((prev) => [...prev, slot]);
    void upload(slot, 'DELIVERY_ITEM_PHOTO');
  }

  function addSignature(blob: Blob): void {
    setLocalError(null);
    const problem = validateBeforeUpload(
      { mimeType: blob.type, sizeBytes: blob.size },
      'signature',
      0,
    );
    if (problem) {
      setLocalError(problem);
      return;
    }
    // Satu tanda tangan saja — menandatangani ulang menggantikan yang sebelumnya.
    const localId = `s-${Date.now()}`;
    blobs.current.set(localId, blob);
    const slot = newSlot({
      localId,
      kind: 'signature',
      fileName: 'tanda-tangan.png',
      sizeBytes: blob.size,
      mimeType: blob.type,
    });
    setSlots((prev) => [...prev.filter((s) => s.kind !== 'signature'), slot]);
    void upload(slot, 'DELIVERY_SIGNATURE');
  }

  function retry(slot: UploadSlot): void {
    const fresh = retrySlot(slot);
    patch(slot.localId, () => fresh);
    void upload(fresh, slot.kind === 'signature' ? 'DELIVERY_SIGNATURE' : 'DELIVERY_ITEM_PHOTO');
  }

  if (state.status === 'loading') return <LoadingState label="Memuat tugas…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} />;

  const task = state.data;
  // Konservatif: bila jenis tugas tidak memberi tahu, perlakukan bukti sebagai WAJIB. Salah
  // menuntut bukti hanya merepotkan; salah melewatkannya menghasilkan tugas selesai tanpa bukti.
  /*
   * Tanda tangan TIDAK mengunci penyelesaian — keputusan Owner 27 Agustus 2026: "yang penting
   * foto". Padnya tetap ditampilkan di bawah; kurir yang memang mengambil tanda tangan tetap
   * dapat melampirkannya.
   */
  /*
   * `statusTugas` diteruskan supaya penghalang "belum tiba" punya keterangan di layar. Sebelumnya
   * `canFinish` mematikan tombol untuk keadaan ini tanpa satu pun kalimat yang menjelaskannya.
   */
  const requirements = {
    requiresProof: true,
    requiresSignature: false,
    recipientName,
    statusTugas: task.status,
  };
  const blockers = completionBlockers(slots, requirements);
  const ready = canComplete(slots, requirements);
  const canFinish = task.status === 'arrived';

  async function finish(): Promise<void> {
    setError(null);
    if (!ready) return;
    setCompleting(true);
    try {
      await completeTask(session.auth, task.id, keys.current(), {
        recipientName: recipientName.trim(),
        recipientRelation: recipientRelation.trim() || null,
        signatureFileId: uploadedSignatureId(slots),
        photoFileIds: uploadedPhotoIds(slots),
        latitude: geo.location.latitude,
        longitude: geo.location.longitude,
        accuracyMeters: geo.location.accuracyMeters,
        note: note.trim() || null,
      });
      keys.rotate();
      navigate(`/tasks/${task.id}`);
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setCompleting(false);
    }
  }

  return (
    <>
      <section className="card">
        <Link to={`/tasks/${task.id}`}>← Detail tugas</Link>
        <h2>Bukti pengiriman</h2>
        <p className="muted small">
          {task.taskNumber} · {task.title}
        </p>
      </section>

      {!canFinish ? (
        <section className="card" role="status">
          <p className="warning">Tugas belum sampai di lokasi.</p>
          <p className="muted small">
            Tekan &quot;Sampai di lokasi&quot; lebih dulu di halaman detail sebelum merekam bukti.
          </p>
        </section>
      ) : null}

      <section className="card">
        <h3>Foto bukti</h3>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          disabled={!canFinish}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) addPhoto(file);
            e.target.value = '';
          }}
        />
        {localError ? (
          <p className="warning small" role="alert">
            {localError}
          </p>
        ) : null}

        <ul className="list">
          {slots.map((slot) => (
            <li key={slot.localId} className="list-item">
              <div className="row space-between">
                <span>{slot.kind === 'signature' ? 'Tanda tangan' : slot.fileName}</span>
                <span className={slot.phase === 'failed' ? 'badge warn' : 'badge'}>
                  {phaseLabel(slot)}
                </span>
              </div>
              {slot.phase === 'uploading' ? (
                <progress max={100} value={slot.progress} aria-label="Progres unggah" />
              ) : null}
              {slot.phase === 'failed' ? (
                <>
                  <p className="warning small">{slot.errorMessage}</p>
                  <button type="button" onClick={() => retry(slot)}>
                    Ulangi berkas ini
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h3>Tanda tangan penerima</h3>
        <SignaturePad onCapture={addSignature} disabled={!canFinish} />
      </section>

      <section className="card">
        <h3>Penerima</h3>
        <label htmlFor="recipient">Nama penerima</label>
        <input
          id="recipient"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          disabled={!canFinish}
        />
        <label htmlFor="relation">Hubungan dengan pelanggan (opsional)</label>
        <input
          id="relation"
          value={recipientRelation}
          onChange={(e) => setRecipientRelation(e.target.value)}
          disabled={!canFinish}
        />
        <label htmlFor="note">Catatan (opsional)</label>
        <input
          id="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={!canFinish}
        />
        <p className={geo.status === 'ready' ? 'small' : 'warning small'}>
          {formatAccuracy(geo.location.accuracyMeters)}
        </p>
      </section>

      {error ? <ErrorCard error={error} /> : null}

      <section className="card">
        {/* Alasan terkunci DIKATAKAN satu per satu — tombol mati tanpa sebab akan ditekan
            berulang kali oleh orang yang mengira aplikasinya bermasalah. */}
        {blockers.length > 0 ? (
          <ul className="list">
            {blockers.map((blocker) => (
              <li key={blocker.code} className="muted small">
                {blocker.message}
              </li>
            ))}
          </ul>
        ) : null}
        <button
          type="button"
          className="button-primary"
          disabled={!canFinish || !ready || completing}
          onClick={() => void finish()}
        >
          {completing ? 'Menyelesaikan…' : 'Selesaikan tugas'}
        </button>
      </section>
    </>
  );
}

function phaseLabel(slot: UploadSlot): string {
  if (isUploaded(slot)) return 'Terunggah';
  switch (slot.phase) {
    case 'idle':
      return 'Menunggu';
    case 'hashing':
      return 'Memeriksa berkas';
    case 'requesting-target':
      return 'Menyiapkan unggahan';
    case 'uploading':
      return `Mengunggah ${slot.progress}%`;
    case 'failed':
      return 'Gagal';
    default:
      return slot.phase;
  }
}
